import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loginSuccess, setPermissions, User } from '../store/slices/authSlice';
import { getUsersRolesAndPermissions, isSuperAdmin } from '../utils/getUsersRoles';
import {
  getUserProfileWithAccessToken,
  getLastAuthErrorType,
  setExtendedToken,
  resetAppAuthService,
  hasValidExtendedToken,
  setTokenProvider
} from '../services/appAuthService';
import {
  Auth0UserWithRoles,
  mapAuth0UserToAppUser,
  storeAccessToken,
  clearStoredAccessToken,
  setAuthFailed
} from '../utils/auth0Utils';
import authService from '../services/authService';
import { STORAGE_KEYS } from '../utils/storageKeys';


/**
 * Custom hook that handles Auth0 authentication and role-based redirection
 * This consolidated hook handles all authentication scenarios including:
 * - Initial login redirection
 * - User status updates
 * - Role-based navigation
 */
const useAuth0Redirect = (forceUpdate = false) => {
  const { isAuthenticated, user, getAccessTokenSilently, logout: auth0Logout } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Use a ref to track if we've already redirected
  const hasRedirectedRef = useRef(false);

  // Set the token provider for the appAuthService
  useEffect(() => {
    if (isAuthenticated) {
      setTokenProvider(getAccessTokenSilently);
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Handle authentication and redirection
  useEffect(() => {
    const handleAuthentication = async () => {
      // Skip if already redirecting or if we've already redirected
      if (isRedirecting || hasRedirectedRef.current) return;
      
      // Skip authentication handling on invitation page
      if (location.pathname === '/invite') return;
      
      // Skip if invitation parameters are present in URL (will be redirected by InvitationDetector)
      const params = new URLSearchParams(location.search);
      if (params.get('invitation') && params.get('organization')) return;
      
      // Check if the user was forcefully logged out due to authentication failure
      const wasForceLoggedOut = sessionStorage.getItem(STORAGE_KEYS.AUTH0_LOGOUT_FORCED) === 'true';

      if (wasForceLoggedOut) {
        console.log('User was forcefully logged out due to authentication failure. Preventing automatic login.');
        // Clear the flag
        sessionStorage.removeItem(STORAGE_KEYS.AUTH0_LOGOUT_FORCED);
        // Set auth failed flag to block UI
        setAuthFailed(true);
        // Force logout again to ensure the user is completely logged out
        auth0Logout({
          logoutParams: {
            returnTo: window.location.origin
          }
        });
        return;
      }
      
      if (isAuthenticated && user) {
        try {
          setIsRedirecting(true);
          
          // Get the initial access token from Auth0
          let initialAccessToken = await getAccessTokenSilently();
          
          // Get the user profile and extended access token from the backend
          // The singleton ensures we only make one API call
          console.log('useAuth0Redirect: Getting user profile with access token');
          let userProfileResponse = await getUserProfileWithAccessToken(initialAccessToken);
          
          // Account was deactivated by an admin - the interceptor already
          // toasted and is forcing a logout. Don't retry, don't fall back
          // into the app.
          if (!userProfileResponse && getLastAuthErrorType() === 'account_inactive') {
            return;
          }

          // Retry once with fresh token if first attempt fails
          if (!userProfileResponse) {
            console.warn('First token exchange failed, retrying with fresh Auth0 token...');
            try {
              initialAccessToken = await getAccessTokenSilently({ cacheMode: 'off' });
              userProfileResponse = await getUserProfileWithAccessToken(initialAccessToken, true);
            } catch (retryError) {
              console.error('Retry failed:', retryError);
            }
          }

          if (!userProfileResponse && getLastAuthErrorType() === 'account_inactive') {
            return;
          }

          if (!userProfileResponse) {
            throw new Error("Failed to get user profile after retry");
          }
          
          // Extract the extended access token from the response
          const extendedAccessToken = userProfileResponse.access_token;
          
          if (!extendedAccessToken) {
            throw new Error("Extended access token not found in response");
          }
          
          // Store the extended token in localStorage and global variable for other services to use
          storeAccessToken(extendedAccessToken);
          setExtendedToken(extendedAccessToken);
          
          // Extract roles and permissions directly from the token
          const { roles, permissions } = getUsersRolesAndPermissions(extendedAccessToken);
          
          // Determine user role
          // If roles array is empty, default to 'user'
          const userRole = !roles || roles.length === 0
            ? 'user'
            : isSuperAdmin(roles)
              ? 'SuperAdmin'
              : roles[0].name;
          console.log('User roles:', roles, 'Determined role:', userRole);
          
          // Store permissions in Redux
          dispatch(setPermissions(permissions));
          
          // Map Auth0 user to app user with the role
          const appUser = mapAuth0UserToAppUser(user as Auth0UserWithRoles, userRole);
          
          // Store user, token, and user profile data in Redux
          dispatch(loginSuccess({
            user: appUser,
            accessToken: extendedAccessToken,
            companyId: userProfileResponse.user?.company
          }));
          
          // Set the redirected flag to true to prevent future redirects
          hasRedirectedRef.current = true;
          
          // Check if this is the first login (after authentication)
          const isFirstLogin = location.pathname === '/';
          
          if (isFirstLogin) {
            navigate('/dashboard', { replace: true });
          }
        } catch (error) {
          console.error('Error handling authentication:', error);
          // If there's an error, redirect to dashboard as fallback
          if (location.pathname === '/') {
            hasRedirectedRef.current = true;
            navigate('/dashboard', { replace: true });
          }
        } finally {
          setIsRedirecting(false);
        }
      }
    };

    // Only run authentication handling at root path or when forced
    // Let AuthReadinessContext handle the token exchange
    if ((location.pathname === '/' && !hasRedirectedRef.current) || (forceUpdate && location.pathname === '/')) {
      handleAuthentication();
    }
  }, [isAuthenticated, user, getAccessTokenSilently, dispatch, navigate, location, isRedirecting, forceUpdate]);

  // Create a wrapper for logout that also clears localStorage and resets the singleton
  const handleLogout = useCallback(() => {
    // Clear the access token from localStorage
    clearStoredAccessToken();
    
    // Reset the extended token in the appAuthService singleton
    resetAppAuthService();
    
    // Call Auth0 logout
    auth0Logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  }, [auth0Logout]);

  // Get the extended access token (with roles and permissions)
  const getToken = async () => {
    if (!isAuthenticated) {
      throw new Error('User is not authenticated');
    }
    
    // Check if we already have a valid extended token
    if (hasValidExtendedToken()) {
      // Return the stored extended token from appAuthService
      return Promise.resolve(localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || '');
    }
    
    // If not, get a new one from Auth0 and exchange it for an extended token
    const initialAccessToken = await getAccessTokenSilently();
    const userProfileResponse = await getUserProfileWithAccessToken(initialAccessToken);
    
    if (!userProfileResponse || !userProfileResponse.access_token) {
      throw new Error('Failed to get extended access token');
    }
    
    // Store the extended token
    storeAccessToken(userProfileResponse.access_token);
    setExtendedToken(userProfileResponse.access_token);
    
    return userProfileResponse.access_token;
  };

  // Update user profile
  const updateProfile = async (userData: Partial<User>) => {
    if (!isAuthenticated) {
      throw new Error('User is not authenticated');
    }
    
    try {
      // Use the extended token for the API call
      const token = await getToken();
      return await authService.updateProfile(userData, () => Promise.resolve(token));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update profile';
      throw new Error(errorMessage);
    }
  };

  return {
    isAuthenticated,
    isLoading: isRedirecting,
    error: null,
    user,
    isRedirecting,
    logout: handleLogout,
    // Get the user profile from the singleton
    getUserProfile: async () => {
      if (isAuthenticated) {
        // Use the extended token
        const token = await getToken();
        return getUserProfileWithAccessToken(token);
      }
      return null;
    },
    getToken,
    updateProfile
  };
};

export default useAuth0Redirect;